import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, RotateCcw, ShieldCheck, Loader2, Coins, Info, Sparkles, CreditCard, Settings } from "lucide-react";
import api from "@/lib/api";

const KIND_LABEL = {
  song: "Canción", video: "Video", shortfilm: "Cortometraje", lipsync: "Lip-sync",
  remix: "Remezcla", extend: "Extensión", pack: "Compra de pack", plan: "Plan mensual", topup: "Recarga",
};

const ACTIONS = [
  { label: "Canción", credits: 4 },
  { label: "Escena de video · 5s", credits: 6 },
  { label: "Escena de video · 10s", credits: 11 },
  { label: "Lip-sync (LatentSync) por escena", credits: 4 },
  { label: "Imagen de personaje", credits: 1 },
];

function StatCard({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="mfp-card p-4 flex items-center gap-3" data-testid={`credits-stat-${label}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tone}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="mono-label">{label}</div>
        <div className="font-head text-xl font-bold">{value}</div>
        {sub && <div className="text-[11px] text-[#64748B]">{sub}</div>}
      </div>
    </div>
  );
}

export default function CreditsPanel() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get("/credits/summary"), api.get("/credits/ledger?limit=100")]);
      setSummary(s.data);
      setLedger(l.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir la gestión de suscripción");
      setPortalBusy(false);
    }
  };

  const periodEnd = summary?.period_end ? new Date(summary.period_end).toLocaleDateString("es", { day: "2-digit", month: "long", year: "numeric" }) : null;

  return (
    <div className="space-y-6" data-testid="credits-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-head text-2xl font-bold">Créditos</h2>
          <p className="text-sm text-[#94A3B8] mt-1">Tu saldo, tu plan y todo tu historial — a la vista.</p>
        </div>
        <button data-testid="buy-credits-btn" onClick={() => navigate("/precios")}
          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors">
          <Sparkles className="w-4 h-4" /> Comprar créditos
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={Coins} label="Saldo de créditos" tone="bg-[#00F0FF]/12 text-[#00F0FF]"
          value={summary ? `${summary.credits} cr` : "—"}
          sub={summary ? `≈ $${(summary.credits * (summary.credit_usd || 0.1)).toFixed(2)}` : null} />
        <StatCard icon={Wallet} label="Plan mensual" tone="bg-[#FF334B]/12 text-[#FF334B]"
          value={summary?.plan_name || "—"}
          sub={summary && summary.plan !== "free" ? `${summary.plan_credits}/${summary.plan_allotment} cr este mes` : (summary ? `${summary.plan_credits} cr` : null)} />
        <StatCard icon={CreditCard} label="Créditos de packs" tone="bg-[#FF9F2E]/12 text-[#FF9F2E]"
          value={summary ? `${summary.pack_credits} cr` : "—"} sub="No expiran" />
      </div>

      {summary?.has_subscription && (
        <div className="mfp-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="subscription-box">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="font-head font-semibold">Suscripción {summary.plan_name} activa</span>
            </div>
            <p className="text-sm text-[#94A3B8] mt-1">
              {periodEnd ? `Se renueva el ${periodEnd} · ${summary.plan_allotment} créditos nuevos cada mes.` : "Renovación automática cada mes."}
            </p>
          </div>
          <button data-testid="manage-subscription-btn" onClick={openPortal} disabled={portalBusy}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-white/15 hover:border-white/30 transition-colors disabled:opacity-60">
            {portalBusy ? <Loader2 className="w-4 h-4 spin" /> : <Settings className="w-4 h-4" />} Administrar suscripción
          </button>
        </div>
      )}

      <div className="mfp-card p-5" data-testid="credits-actions-table">
        <h3 className="font-head font-semibold mb-3">Costo por acción</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 divide-y sm:divide-y-0 divide-white/8">
          {ACTIONS.map((a, i) => (
            <div key={i} className="flex items-center justify-between py-2.5">
              <span className="text-sm text-[#94A3B8]">{a.label}</span>
              <span className="inline-flex items-center gap-1.5 font-semibold"><Coins className="w-3.5 h-3.5 text-[#FF9F2E]" />{a.credits} cr</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mfp-card p-5">
        <h3 className="font-head font-semibold mb-1">Historial de movimientos</h3>
        <p className="text-xs text-[#64748B] mb-4 inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" /> Cada cargo, compra y reembolso de créditos. Si un trabajo falla por un problema técnico, se reembolsa automáticamente.
        </p>
        {ledger.length === 0 ? (
          <div className="text-center py-10 text-[#64748B]" data-testid="ledger-empty">
            <Coins className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Aún no hay movimientos. Genera tu primera canción o video.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="ledger-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#64748B] border-b border-white/8">
                  <th className="py-2 font-medium">Fecha</th>
                  <th className="py-2 font-medium">Concepto</th>
                  <th className="py-2 font-medium">Tipo</th>
                  <th className="py-2 font-medium text-right">Créditos</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((r) => {
                  const positive = r.type === "refund" || r.type === "grant";
                  return (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`ledger-row-${r.type}`}>
                      <td className="py-2.5 text-[#94A3B8] whitespace-nowrap">{new Date(r.created_at).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="py-2.5">
                        <span className="font-medium">{r.project_title || KIND_LABEL[r.kind] || "—"}</span>
                        {r.project_title && <span className="text-[#64748B] ml-1.5 text-xs">{KIND_LABEL[r.kind] || r.kind}</span>}
                      </td>
                      <td className="py-2.5">
                        {r.type === "refund" ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><RotateCcw className="w-3 h-3" /> Reembolso{r.note ? ` · ${r.note}` : ""}</span>
                        ) : r.type === "grant" ? (
                          <span className="text-[#00F0FF] text-xs font-semibold">{r.note || "Recarga"}</span>
                        ) : (
                          <span className="text-[#94A3B8] text-xs font-semibold">Cargo</span>
                        )}
                      </td>
                      <td className={`py-2.5 text-right font-semibold ${positive ? "text-emerald-400" : "text-white"}`}>
                        {positive ? "+" : "−"}{r.credits} cr
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
